import { Button } from '@talentmesh/ui/components/button';
import { DropdownMenuSeparator } from '@talentmesh/ui/components/dropdown-menu';
import { cn } from '@talentmesh/ui/lib/utils';

import { useMemo, useState } from 'react';

import { type MRT_Column, type MRT_RowData, type MRT_TableInstance } from '../../types';
import { getDefaultColumnOrderIds } from '../../utils/displayColumn.utils';
import classes from './MRT_ShowHideColumnsMenu.module.css';
import { MRT_ShowHideColumnsMenuItems } from './MRT_ShowHideColumnsMenuItems';

interface Props<TData extends MRT_RowData> {
    table: MRT_TableInstance<TData>;
}

export const MRT_ShowHideColumnsMenu = <TData extends MRT_RowData>({ table }: Props<TData>) => {
    const {
        getAllColumns,
        getAllLeafColumns,
        getCenterLeafColumns,
        getIsAllColumnsVisible,
        getIsSomeColumnsPinned,
        getIsSomeColumnsVisible,
        getStartLeafColumns,
        getEndLeafColumns,
        getState,
        options: { enableColumnOrdering, enableColumnPinning, enableHiding, localization },
    } = table;
    const { columnOrder, columnPinning } = getState();

    const handleToggleAllColumns = (value?: boolean) => {
        getAllLeafColumns()
            .filter((col) => col.columnDef.enableHiding !== false)
            .forEach((col) => col.toggleVisibility(value));
    };

    const allColumns = useMemo(() => {
        const columns = getAllColumns();
        if (columnOrder.length > 0 && !columns.some((col) => col.columnDef.columnDefType === 'group')) {
            return [
                ...getStartLeafColumns(),
                ...Array.from(new Set(columnOrder)).map((colId) =>
                    getCenterLeafColumns().find((col) => col?.id === colId),
                ),
                ...getEndLeafColumns(),
            ].filter(Boolean);
        }
        return columns;
    }, [
        columnOrder,
        columnPinning,
        getAllColumns(),
        getCenterLeafColumns(),
        getStartLeafColumns(),
        getEndLeafColumns(),
    ]) as MRT_Column<TData>[];

    const [hoveredColumn, setHoveredColumn] = useState<MRT_Column<TData> | null>(null);

    return (
        <div className={cn(classes.root)}>
            <div className={cn('flex flex-wrap gap-1', classes.content)}>
                {enableHiding && (
                    <Button
                        disabled={!getIsSomeColumnsVisible()}
                        onClick={() => handleToggleAllColumns(false)}
                        size="sm"
                        variant="ghost"
                    >
                        {localization.hideAll}
                    </Button>
                )}
                {enableColumnOrdering && (
                    <Button
                        onClick={() => table.setColumnOrder(getDefaultColumnOrderIds(table.options as any, true))}
                        size="sm"
                        variant="ghost"
                    >
                        {localization.resetOrder}
                    </Button>
                )}
                {enableColumnPinning && (
                    <Button
                        disabled={!getIsSomeColumnsPinned()}
                        onClick={() => table.resetColumnPinning(true)}
                        size="sm"
                        variant="ghost"
                    >
                        {localization.unpinAll}
                    </Button>
                )}
                {enableHiding && (
                    <Button
                        disabled={getIsAllColumnsVisible()}
                        onClick={() => handleToggleAllColumns(true)}
                        size="sm"
                        variant="ghost"
                    >
                        {localization.showAll}
                    </Button>
                )}
            </div>
            <DropdownMenuSeparator />
            {allColumns.map((column, index) => (
                <MRT_ShowHideColumnsMenuItems
                    allColumns={allColumns}
                    column={column}
                    hoveredColumn={hoveredColumn}
                    key={`${index}-${column.id}`}
                    setHoveredColumn={setHoveredColumn}
                    table={table}
                />
            ))}
        </div>
    );
};
